<template>
  <div class="bundle-section-container">
    <div class="bundle-section">
      <div class="bundle-images">
        <div class="bundle-image">
          <PreserveRatio :width="2890" :height="1500" :key="media.url">
            <div
              v-if="media && media.type === 'IMAGE'"
              class="bundle-image-src"
              :key="media?.url"
              @click="openBundleGallery('New_Gallery_Image_Clicked')"
            >
              <ResponsiveImage
                :url="`${media.url}?fit=max&w={width}&q={q}`"
                :lazy="false"
                :widths="[768, 930, 1050, 1370, 1370]"
                :breakpoints="[320, 768, 970, 1400, 1600]"
                :alt="altText"
              ></ResponsiveImage>
            </div>
            <VideoPlayer
              v-else-if="media && media.type === 'VIDEO'"
              :key="media.url"
              :auto-load="true"
              :active="true"
              :poster="media.thumbnailUrl"
              :hls="media.hlsUrl"
              :dash="media.dashUrl"
              :mp4="media.url"
              :product="media.product"
            ></VideoPlayer>
            <div class="bundle-image-left" @click="swipeRight">
              <button class="button white filled icon" aria-label="Previous gallery image"><span class="af af-chevron-left"></span></button>
            </div>
            <div class="bundle-image-right" @click="swipeLeft">
              <button class="button white filled icon" aria-label="Next gallery image"><span class="af af-chevron-right"></span></button>
            </div>
            <ProductImageControls
              v-if="media && media.type === 'IMAGE'"
              :medias="media"
              @zoom="openBundleGallery('New_Gallery_Magnify_Zoom_Clicked')"
              class="bundle-image-controls"
            ></ProductImageControls>
          </PreserveRatio>
        </div>
        <ProductThumbnails
          :medias="medias"
          :active-media="activeMedia"
          @clickThumbnail="updateActiveMedia"
          ref="bundleThumbnails"
        ></ProductThumbnails>
      </div>
    </div>
  </div>
</template>

<script>
import PreserveRatio from '@/components/commons/PreserveRatio.vue';
import { Divider, MediaUtils, Swipe } from '@/pixel';
import ResponsiveImage from '@/components/commons/ResponsiveImage.vue';
import ZoomIcon from '@/assets/product/zoom-in.svg';
import VideoPlayer from '@/components/product/Video.vue';
import ProductUnavailableMessage from '@/components/product/ProductUnavailableMessage.vue';
import ProductThumbnails from '@/components/product/ProductThumbnails.vue';
import { computed, ref } from 'vue';
import ProductImageControls from '@/components/product/ProductImageControls.vue';
import { useRouter, useRoute } from 'vue-router';

export default {
  name: 'BundleSet',
  components: {
    PreserveRatio,
    Divider,
    ResponsiveImage,
    ZoomIcon,
    VideoPlayer,
    ProductUnavailableMessage,
    ProductThumbnails,
    ProductImageControls,
  },

  props: {
    medias: {
      type: Array,
      required: true,
      default: [],
    },
    activeMedia: {
      default: 0,
      type: Number,
    },
  },

  emits: ['updateActiveMedia', 'trackEvents'],

  async setup(props, context) {
    const router = useRouter();
    const route = useRoute();
    const bundleThumbnails = ref(null);
    const media = computed(() => {
      const index = props.activeMedia;
      if (props.medias.length) {
        return props.medias[index] || {};
      }
      return {};
    });

    const mediaLength = computed(() => {
      if (props.medias) {
        return props.medias?.length;
      }
      return 0;
    });

    const altText = computed(() => {
      return `${props.medias[props.activeMedia]?.alt ?? 'Bundle Image'} - Primary View ${props.activeMedia + 1} of ${
        props.medias.length
      } (Open Fullscreen View).`;
    });

    function swipeLeft() {
      updateActiveMedia(props.activeMedia < mediaLength.value - 1 ? props.activeMedia + 1 : 0);
    }

    function swipeRight() {
      updateActiveMedia(props.activeMedia > 0 ? props.activeMedia - 1 : mediaLength.value - 1);
    }
    function handleKeydown(e) {
      const isGalleryOpen = window.location.search.includes('bundleGallery');
      const isTargetInput = e.target.tagName === 'INPUT';

      if (!isGalleryOpen && !isTargetInput) {
        switch (e.key) {
          case 'ArrowLeft':
            swipeRight();
            break;
          case 'ArrowRight':
            swipeLeft();
            break;
          case '+':
          case '=':
           openBundleGallery('New_Gallery_Image_Clicked');
            break;
        }
      }
    }

    function updateActiveMedia(index) {
      context.emit('trackEvents', 'New_Gallery_Thumbnail_Clicked');
      context.emit('updateActiveMedia', index);
      if (bundleThumbnails.value) {
        bundleThumbnails.value.scrollToThumbnail(index);
      }
    }

    async function openBundleGallery(event) {
      context.emit('trackEvents', event);

      if (window.location.href.includes('product')) {
        await router.push({
          name: 'product',
          params: {
            id: route.params.id,
            slug: route.params.slug,
          },
          query: { bundleGallery: props.activeMedia },
        });
      } else {
        await router.push({
          name: 'set',
          params: {
            id: route.params.id,
            slug: route.params.slug,
          },
          query: { bundleGallery: props.activeMedia },
        });
      }
    }

    return {
      media,
      mediaLength,
      mobile: MediaUtils.isMobileAndSmaller,
      router,
      swipeLeft,
      swipeRight,
      handleKeydown,
      updateActiveMedia,
      openBundleGallery,
      bundleThumbnails,
      route,
      altText,
    };
  },

  beforeMount() {
    window.addEventListener('keydown', this.handleKeydown);

    if (!import.meta.env.SSR) {
      this.swipe = new Swipe();
      this.swipe.setOnLeft(() => this.swipeLeft());
      this.swipe.setOnRight(() => this.swipeRight());
    }
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';

.bundle-section {
  display: flex;
  width: 100%;
  flex-direction: column;
  margin-bottom: var(--space-6);

  @media (min-width: $desktop-breakpoint) {
    flex-direction: row;
  }
}

.bundle-images {
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: auto;
  min-width: 280px;

  @media (max-width: calc($tablet-breakpoint - 1px)) {
    margin-left: calc(-1 * var(--space-5));
    margin-right: calc(-1 * var(--space-5));
  }
}

.bundle-image {
  position: relative;
}

.bundle-image-src {
  cursor: zoom-in;
}
.bundle-image-left,
.bundle-image-right {
  position: absolute;
  top: 80px;
  bottom: 80px;
  display: flex;
  align-items: center;
  padding: var(--space-10);
  opacity: 0;
  transition: opacity 150ms ease-in-out;
  /* purgecss ignore */
  &:hover {
    opacity: 1;
  }
}

.bundle-image-left {
  left: 0;
  padding-left: var(--space-4);
}

.bundle-image-right {
  right: 0;
  padding-right: var(--space-4);
}
</style>
